import { useState, useEffect } from 'react'
import { useAppStore } from '../../store/app'
import { fetchTempAgentDefaults, setTempAgentDefaults, wsRequest } from '../../api/ws'
import { SectionHeader, Group } from '../account/AccountComponents'
import '../account/AccountStyles.css'

type Harness = { id: string; name: string }
type AIBrainModel = { id: string; name: string; provider?: string }

export function TempAgentDefaultsSection() {
  const hosts = useAppStore((s) => s.hosts)
  const organizationID = useAppStore((s) => s.organizationID)
  const setTempAgentDefaultsStore = useAppStore((s) => s.setTempAgentDefaultsStore)

  const [hostID, setHostID] = useState<string>('')
  const [harnessID, setHarnessID] = useState<string>('')
  const [modelID, setModelID] = useState<string>('')

  const [harnesses, setHarnesses] = useState<Harness[]>([])
  const [models, setModels] = useState<AIBrainModel[]>([])

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  // Load current defaults on mount.
  useEffect(() => {
    if (!organizationID) return
    fetchTempAgentDefaults(organizationID)
      .then((r) => {
        if (!r.error) {
          setHostID(r.defaults.host_id ?? '')
          setHarnessID(r.defaults.harness_id ?? '')
          setModelID(r.defaults.ai_brain_model_id ?? '')
        }
      })
      .catch(() => {})
  }, [organizationID])

  // When host changes, reload harnesses available on that host.
  useEffect(() => {
    if (!organizationID || !hostID) {
      setHarnesses([])
      return
    }
    void wsRequest<{ harnesses?: Harness[]; available_harness_ids?: string[]; error?: string }>(
      'list_harnesses',
      { host_id: hostID, organization_id: organizationID },
      'harnesses',
      { kind: 'organization', id: organizationID, action: 'list_harnesses' },
    ).then((r) => {
      const all = r.harnesses ?? []
      const available = new Set(r.available_harness_ids ?? [])
      // Only show harnesses installed on this host.
      setHarnesses(all.filter((h) => available.size === 0 || available.has(h.id)))
      // If the current harnessID is no longer available, clear it.
      if (harnessID && !all.find((h) => h.id === harnessID)) {
        setHarnessID('')
        setModelID('')
      }
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationID, hostID])

  // When harness changes, reload models for that harness.
  useEffect(() => {
    if (!organizationID || !harnessID) {
      setModels([])
      return
    }
    void wsRequest<{ ai_brain_models?: AIBrainModel[]; error?: string }>(
      'list_ai_brain_models',
      { organization_id: organizationID, harness_id: harnessID },
      'ai_brain_models',
      { kind: 'organization', id: organizationID, action: 'list_ai_brain_models' },
    ).then((r) => {
      setModels(r.ai_brain_models ?? [])
      if (modelID && !(r.ai_brain_models ?? []).find((m) => m.id === modelID)) {
        setModelID('')
      }
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationID, harnessID])

  async function save(newHostID: string, newHarnessID: string, newModelID: string) {
    if (!organizationID) return
    setSaving(true)
    setSaveError(null)
    setSavedAt(null)
    try {
      const r = await setTempAgentDefaults(organizationID, {
        host_id: newHostID || null,
        harness_id: newHarnessID || null,
        ai_brain_model_id: newModelID || null,
      })
      if (r.error) {
        setSaveError(r.error)
      } else {
        setSavedAt(Date.now())
        setTempAgentDefaultsStore({
          host_id: r.defaults?.host_id ?? null,
          harness_id: r.defaults?.harness_id ?? null,
          ai_brain_model_id: r.defaults?.ai_brain_model_id ?? null,
          harness_name: r.defaults?.harness_name ?? null,
        })
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  function onHostChange(id: string) {
    setHostID(id)
    setHarnessID('')
    setModelID('')
    setSavedAt(null)
    setSaveError(null)
    if (id) void save(id, '', '')
  }

  function onHarnessChange(id: string) {
    setHarnessID(id)
    setModelID('')
    setSavedAt(null)
    setSaveError(null)
    if (id) void save(hostID, id, '')
  }

  function onModelChange(id: string) {
    setModelID(id)
    setSavedAt(null)
    setSaveError(null)
    void save(hostID, harnessID, id)
  }

  const selectedHost = hosts.find((h) => h.host_id === hostID)
  const selectedHarness = harnesses.find((h) => h.id === harnessID)
  const selectedModel = models.find((m) => m.id === modelID)

  // Determine whether this harness uses a model (claude-code, codex do; others don't).
  const harnessUsesModel = selectedHarness
    ? selectedHarness.name === 'claude-code' || selectedHarness.name === 'codex'
    : false

  return (
    <>
      <SectionHeader>Default Temp Agent</SectionHeader>
      <Group>
        <div className="about__row">
          <span className="about__row-label">Host</span>
          <select
            className="about__row-input"
            value={hostID}
            onChange={(e) => onHostChange(e.target.value)}
            disabled={saving}
          >
            <option value="">— not set —</option>
            {hosts.map((h) => (
              <option key={h.host_id} value={h.host_id}>
                {h.hostname || h.host_id}{h.online ? '' : ' (offline)'}
              </option>
            ))}
          </select>
        </div>

        {hostID && (
          <div className="about__row">
            <span className="about__row-label">Harness</span>
            <select
              className="about__row-input"
              value={harnessID}
              onChange={(e) => onHarnessChange(e.target.value)}
              disabled={saving || harnesses.length === 0}
            >
              <option value="">— not set —</option>
              {harnesses.map((h) => (
                <option key={h.id} value={h.id}>{h.name}</option>
              ))}
            </select>
            {harnesses.length === 0 && hostID && (
              <span className="about__detail" style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
                No harnesses on this host
              </span>
            )}
          </div>
        )}

        {harnessID && harnessUsesModel && (
          <div className="about__row">
            <span className="about__row-label">Model</span>
            <select
              className="about__row-input"
              value={modelID}
              onChange={(e) => onModelChange(e.target.value)}
              disabled={saving || models.length === 0}
            >
              <option value="">— not set —</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        )}

        {saving && <div className="about__hint">Saving…</div>}
        {!saving && savedAt && !saveError && (
          <div className="about__hint" style={{ color: 'var(--success)' }}>
            {selectedHost?.hostname || 'Host'} · {selectedHarness?.name || 'Harness'}
            {harnessUsesModel && selectedModel ? ` · ${selectedModel.name}` : ''}
          </div>
        )}
        {saveError && <div className="about__error">{saveError}</div>}
      </Group>
    </>
  )
}
